import { FunctionComponent } from "react";
import type { KeyboardEvent, MouseEvent, ReactNode } from "react";
import { Box, Chip, Paper, Stack, Tooltip, Typography } from "@mui/material";
import type { PaperProps, SxProps, Theme } from "@mui/material";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import RadioButtonCheckedIcon from "@mui/icons-material/RadioButtonChecked";
import ScheduleIcon from "@mui/icons-material/Schedule";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

type SemanticTone = "neutral" | "info" | "success" | "warning" | "error";

const toneStyles: Record<
  SemanticTone,
  { foreground: string; background: string }
> = {
  neutral: { foreground: "var(--muted)", background: "var(--panel2)" },
  info: { foreground: "var(--blue)", background: "var(--blue-bg)" },
  success: { foreground: "var(--green)", background: "var(--green-bg)" },
  warning: { foreground: "var(--amber)", background: "var(--amber-bg)" },
  error: { foreground: "var(--red)", background: "var(--red-bg)" },
};

export type CockpitCardProps = PaperProps & {
  children: ReactNode;
};

export const CockpitCard: FunctionComponent<CockpitCardProps> = ({
  children,
  sx,
  ...props
}) => (
  <Paper
    variant="outlined"
    {...props}
    sx={[
      {
        borderColor: "var(--line)",
        backgroundColor: "var(--panel)",
        backgroundImage: "none",
        borderRadius: 2,
        p: 2.5,
      },
      ...(sx ? (Array.isArray(sx) ? sx : [sx]) : []),
    ]}
  >
    {children}
  </Paper>
);

export type PageHeaderProps = {
  title: string;
  eyebrow?: ReactNode;
  description?: ReactNode;
  metadata?: ReactNode;
  actions?: ReactNode;
};

export const PageHeader: FunctionComponent<PageHeaderProps> = ({
  title,
  eyebrow,
  description,
  metadata,
  actions,
}) => (
  <Box component="header" className="cockpit-ui">
    <Stack
      direction={{ xs: "column", md: "row" }}
      alignItems={{ xs: "flex-start", md: "center" }}
      justifyContent="space-between"
      spacing={2}
    >
      <Stack spacing={0.5} sx={{ minWidth: 0 }}>
        {eyebrow ? (
          <Typography
            variant="overline"
            sx={{
              color: "var(--blue)",
              fontWeight: 700,
              letterSpacing: "0.1em",
            }}
          >
            {eyebrow}
          </Typography>
        ) : null}
        <Typography component="h1" variant="h4" sx={{ color: "var(--text)" }}>
          {title}
        </Typography>
        {description ? (
          <Typography
            variant="body1"
            sx={{ color: "var(--muted)", maxWidth: 760 }}
          >
            {description}
          </Typography>
        ) : null}
        {metadata ? <Box sx={{ color: "var(--faint)" }}>{metadata}</Box> : null}
      </Stack>
      {actions ? (
        <Stack
          direction="row"
          spacing={1}
          useFlexGap
          flexWrap="wrap"
          sx={{ maxWidth: "100%" }}
        >
          {actions}
        </Stack>
      ) : null}
    </Stack>
  </Box>
);

export type KpiProps = {
  label: string;
  value: ReactNode;
  helper?: ReactNode;
  delta?: ReactNode;
  compact?: boolean;
  meter?: number | null;
  tone?: SemanticTone;
  onClick?: () => void;
};

export const Kpi: FunctionComponent<KpiProps> = ({
  label,
  value,
  helper,
  delta,
  compact = false,
  meter = null,
  tone = "neutral",
  onClick,
}) => (
  <Stack
    spacing={compact ? 0.25 : 0.75}
    data-testid="cockpit-kpi"
    data-density={compact ? "compact" : "default"}
    onClick={onClick}
    role={onClick ? "button" : undefined}
    tabIndex={onClick ? 0 : undefined}
    onKeyDown={
      onClick
        ? (event: KeyboardEvent) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              onClick();
            }
          }
        : undefined
    }
    sx={onClick ? { cursor: "pointer" } : undefined}
  >
    <Typography
      variant="overline"
      sx={{
        color: "var(--muted)",
        fontWeight: 600,
        lineHeight: compact ? 1.25 : undefined,
      }}
    >
      {label}
    </Typography>
    <Typography
      component="div"
      variant="h4"
      sx={{
        color: tone === "neutral" ? "var(--text)" : toneStyles[tone].foreground,
        fontFamily: "var(--font-mono)",
        fontWeight: 600,
        fontSize: compact ? { xs: "1.35rem", md: "1.55rem" } : undefined,
        lineHeight: compact ? 1.1 : undefined,
      }}
    >
      {value}
    </Typography>
    {delta || helper ? (
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
        {delta}
        {helper ? (
          <Typography
            variant="caption"
            sx={{ color: "var(--muted)", lineHeight: compact ? 1.2 : undefined }}
          >
            {helper}
          </Typography>
        ) : null}
      </Stack>
    ) : null}
    {typeof meter === "number" && Number.isFinite(meter) ? (
      <Box
        aria-hidden
        sx={{
          mt: 0.75,
          height: 5,
          borderRadius: 1,
          bgcolor: "var(--panel3)",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            width: `${Math.max(0, Math.min(100, meter))}%`,
            height: "100%",
            bgcolor: toneStyles[tone]?.foreground || "var(--green)",
          }}
        />
      </Box>
    ) : null}
  </Stack>
);

export type DeltaProps = {
  direction: "up" | "down" | "flat";
  value: ReactNode;
  label?: string;
  tone?: SemanticTone;
};

const deltaDirectionLabel: Record<DeltaProps["direction"], string> = {
  up: "Up",
  down: "Down",
  flat: "Unchanged",
};

export const Delta: FunctionComponent<DeltaProps> = ({
  direction,
  value,
  label,
  tone = "neutral",
}) => {
  const Icon =
    direction === "up"
      ? ArrowUpwardIcon
      : direction === "down"
        ? ArrowDownwardIcon
        : ArrowForwardIcon;
  const accessibleLabel = [label, deltaDirectionLabel[direction], value]
    .filter(Boolean)
    .join(": ");

  return (
    <Stack
      component="span"
      direction="row"
      spacing={0.5}
      alignItems="center"
      aria-label={accessibleLabel}
      sx={{
        position: "relative",
        color: toneStyles[tone].foreground,
        fontFamily: "var(--font-mono)",
      }}
    >
      <Icon aria-hidden="true" sx={{ fontSize: 15 }} />
      <Typography
        component="span"
        variant="caption"
        sx={{ color: "inherit", fontFamily: "inherit", fontWeight: 600 }}
      >
        {value}
      </Typography>
      <Box
        component="span"
        sx={{
          position: "absolute",
          width: "1px",
          height: "1px",
          p: 0,
          m: -1,
          overflow: "hidden",
          clip: "rect(0, 0, 0, 0)",
          whiteSpace: "nowrap",
          border: 0,
        }}
      >
        {deltaDirectionLabel[direction]}
      </Box>
    </Stack>
  );
};

export type StatusChipProps = {
  label: string;
  tone?: SemanticTone;
};

const statusIcon = (tone: SemanticTone) => {
  switch (tone) {
    case "success":
      return <CheckCircleOutlineIcon aria-hidden="true" />;
    case "warning":
      return <WarningAmberIcon aria-hidden="true" />;
    case "error":
      return <ErrorOutlineIcon aria-hidden="true" />;
    case "info":
      return <InfoOutlinedIcon aria-hidden="true" />;
    default:
      return <RadioButtonCheckedIcon aria-hidden="true" />;
  }
};

export const StatusChip: FunctionComponent<StatusChipProps> = ({
  label,
  tone = "neutral",
}) => (
  <Chip
    size="small"
    icon={statusIcon(tone)}
    label={label}
    aria-label={`Status: ${label}`}
    sx={{
      color: toneStyles[tone].foreground,
      backgroundColor: toneStyles[tone].background,
      border: `1px solid ${toneStyles[tone].foreground}`,
      fontWeight: 600,
      "& .MuiChip-icon": { color: "inherit" },
    }}
  />
);

export type PipelineStageStatus =
  | "complete"
  | "current"
  | "pending"
  | "hold"
  | "error"
  | "unknown";

export type PipelineStage = {
  id: string;
  label: string;
  status: PipelineStageStatus;
  detail?: ReactNode;
  hover?: ReactNode;
  onActivate?: () => void;
  actionLabel?: string;
};

export type PipelineProps = {
  stages: PipelineStage[];
  ariaLabel?: string;
  orientation?: "vertical" | "horizontal";
};

const pipelineStatusLabel: Record<PipelineStageStatus, string> = {
  complete: "Complete",
  current: "Current",
  pending: "Pending",
  hold: "On hold",
  error: "Error",
  unknown: "Unknown",
};

const pipelineIcon = (status: PipelineStageStatus) => {
  switch (status) {
    case "complete":
      return <CheckCircleOutlineIcon aria-hidden="true" />;
    case "current":
      return <RadioButtonCheckedIcon aria-hidden="true" />;
    case "error":
      return <ErrorOutlineIcon aria-hidden="true" />;
    case "hold":
      return <WarningAmberIcon aria-hidden="true" />;
    default:
      return <ScheduleIcon aria-hidden="true" />;
  }
};

const pipelineTone = (status: PipelineStageStatus): SemanticTone => {
  switch (status) {
    case "complete":
      return "success";
    case "current":
      return "info";
    case "error":
      return "error";
    case "hold":
      return "warning";
    default:
      return "neutral";
  }
};

const pipelineAriaLabel = (stage: PipelineStage) => {
  const statusText = `${stage.label}: ${pipelineStatusLabel[stage.status]}`;
  if (!stage.onActivate) {
    return statusText;
  }
  const action = stage.actionLabel || statusText;
  return action.includes(statusText) ? action : `${action}. ${statusText}`;
};

const PipelineStepControl: FunctionComponent<{
  stage: PipelineStage;
  children: ReactNode;
  sx: SxProps<Theme>;
}> = ({ stage, children, sx }) => (
  <Box
    component={stage.onActivate ? "button" : "div"}
    type={stage.onActivate ? "button" : undefined}
    onClick={stage.onActivate ? (event: MouseEvent) => {
      event.stopPropagation();
      stage.onActivate?.();
    } : undefined}
    aria-label={pipelineAriaLabel(stage)}
    data-pipeline-status={stage.status}
    sx={{
      p: 0,
      border: 0,
      background: "transparent",
      color: "inherit",
      font: "inherit",
      cursor: stage.onActivate ? "pointer" : "default",
      "&:focus-visible": {
        outline: "2px solid var(--focus-ring)",
        outlineOffset: "3px",
      },
      ...sx,
    }}
  >
    {children}
  </Box>
);

const wrapPipelineHover = (stage: PipelineStage, node: ReactNode) => {
  if (!stage.hover) {
    return node;
  }

  return (
    <Tooltip
      title={stage.hover}
      placement="top"
      enterDelay={200}
      slotProps={{
        tooltip: {
          sx: {
            maxWidth: 320,
            bgcolor: "var(--panel)",
            color: "var(--text)",
            border: "1px solid var(--line)",
            boxShadow: "var(--shadow)",
          },
        },
      }}
    >
      <Box component="span" sx={{ display: "block" }}>{node}</Box>
    </Tooltip>
  );
};

const HorizontalPipelineDot: FunctionComponent<{ status: PipelineStageStatus }> = ({
  status,
}) => {
  const tone = pipelineTone(status);
  const isComplete = status === "complete";
  const isError = status === "error";
  const isHold = status === "hold";
  const isCurrent = status === "current";

  return (
    <Box
      aria-hidden="true"
      data-pipeline-dot={status}
      sx={{
        width: 13,
        height: 13,
        borderRadius: "50%",
        border: "2px solid",
        borderColor: isComplete
          ? "var(--green)"
          : isError
            ? "var(--red)"
            : isHold
              ? "var(--amber)"
              : isCurrent
                ? "var(--blue)"
                : "var(--line2)",
        background: isComplete
          ? "var(--green)"
          : isError
            ? "var(--red)"
            : isHold
              ? "var(--amber-bg)"
              : isCurrent
                ? "var(--blue)"
                : "var(--panel2)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: isComplete || isError ? "var(--panel)" : toneStyles[tone].foreground,
        flexShrink: 0,
      }}
    >
      {isComplete ? (
        <Box
          sx={{
            width: 4,
            height: 7,
            border: "solid currentColor",
            borderWidth: "0 2px 2px 0",
            transform: "rotate(45deg) translate(-0.5px, -1px)",
          }}
        />
      ) : isError ? (
        <Box
          sx={{
            position: "relative",
            width: 7,
            height: 7,
            "&:before, &:after": {
              content: '""',
              position: "absolute",
              left: 2.5,
              top: 0,
              width: 2,
              height: 7,
              background: "currentColor",
            },
            "&:before": { transform: "rotate(45deg)" },
            "&:after": { transform: "rotate(-45deg)" },
          }}
        />
      ) : isHold ? (
        <Box sx={{ width: 5, height: 5, borderRadius: "50%", bgcolor: "var(--amber)" }} />
      ) : null}
    </Box>
  );
};

const HorizontalPipeline: FunctionComponent<{
  stages: PipelineStage[];
  ariaLabel: string;
}> = ({ stages, ariaLabel }) => (
  <Stack
    component="ol"
    aria-label={ariaLabel}
    direction="row"
    alignItems="flex-start"
    flexWrap="nowrap"
    sx={{ listStyle: "none", m: 0, p: 0, minWidth: "max-content" }}
  >
    {stages.map((stage, index) => {
      const next = stages[index + 1];
      const connectorDone = stage.status === "complete" && next?.status === "complete";
      return (
        <Box
          component="li"
          key={stage.id}
          sx={{ display: "flex", alignItems: "flex-start", listStyle: "none" }}
        >
          {wrapPipelineHover(
            stage,
            <PipelineStepControl
              stage={stage}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "4px",
                width: 44,
                borderRadius: 0.75,
              }}
            >
              <HorizontalPipelineDot status={stage.status} />
              <Typography
                component="span"
                sx={{
                  fontSize: "8.5px",
                  letterSpacing: "0.8px",
                  color: "var(--faint)",
                  fontWeight: 600,
                  lineHeight: 1.1,
                  textTransform: "uppercase",
                  textAlign: "center",
                  textDecoration: stage.onActivate ? "underline" : "none",
                  textUnderlineOffset: "2px",
                }}
              >
                {stage.label}
              </Typography>
            </PipelineStepControl>,
          )}
          {next ? (
            <Box
              aria-hidden="true"
              data-pipeline-connector={connectorDone ? "done" : "pending"}
              sx={{
                height: 2,
                width: 14,
                bgcolor: connectorDone ? "var(--green)" : "var(--line2)",
                mt: "5.5px",
                ml: "-6px",
                mr: "-6px",
                flexShrink: 0,
              }}
            />
          ) : null}
        </Box>
      );
    })}
  </Stack>
);

export const Pipeline: FunctionComponent<PipelineProps> = ({
  stages,
  ariaLabel = "Progress",
  orientation = "vertical",
}) => {
  if (orientation === "horizontal") {
    return <HorizontalPipeline stages={stages} ariaLabel={ariaLabel} />;
  }

  return (
    <Stack
      component="ol"
      aria-label={ariaLabel}
      direction="column"
      spacing={1}
      sx={{ listStyle: "none", m: 0, p: 0 }}
    >
      {stages.map((stage) => {
        const tone = pipelineTone(stage.status);
        return (
          <Stack component="li" key={stage.id}>
            {wrapPipelineHover(
              stage,
              <PipelineStepControl
                stage={stage}
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1,
                  borderRadius: 0.75,
                  textAlign: "left",
                }}
              >
                <Box sx={{ color: toneStyles[tone].foreground, display: "flex", mt: "2px" }}>
                  {pipelineIcon(stage.status)}
                </Box>
                <Box>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "var(--text)",
                      fontWeight: stage.status === "current" || stage.onActivate ? 700 : 500,
                      textDecoration: stage.onActivate ? "underline" : "none",
                      textDecorationThickness: "1px",
                      textUnderlineOffset: "2px",
                    }}
                  >
                    {stage.label}
                  </Typography>
                  <Typography variant="caption" sx={{ color: toneStyles[tone].foreground }}>
                    {pipelineStatusLabel[stage.status]}
                  </Typography>
                  {stage.detail ? (
                    <Typography variant="caption" component="div" sx={{ color: "var(--muted)" }}>
                      {stage.detail}
                    </Typography>
                  ) : null}
                </Box>
              </PipelineStepControl>,
            )}
          </Stack>
        );
      })}
    </Stack>
  );
};

export type AnnunciatorProps = {
  title: string;
  message: ReactNode;
  tone?: Exclude<SemanticTone, "neutral">;
  action?: ReactNode;
};

const annunciatorIcon = (tone: AnnunciatorProps["tone"]) => {
  switch (tone) {
    case "success":
      return <CheckCircleOutlineIcon aria-hidden="true" />;
    case "warning":
      return <WarningAmberIcon aria-hidden="true" />;
    case "error":
      return <ErrorOutlineIcon aria-hidden="true" />;
    default:
      return <InfoOutlinedIcon aria-hidden="true" />;
  }
};

export const Annunciator: FunctionComponent<AnnunciatorProps> = ({
  title,
  message,
  tone = "info",
  action,
}) => (
  <Paper
    variant="outlined"
    role={tone === "error" || tone === "warning" ? "alert" : "status"}
    sx={{
      borderColor: toneStyles[tone].foreground,
      backgroundColor: toneStyles[tone].background,
      color: "var(--text)",
      p: 1.5,
    }}
  >
    <Stack direction="row" spacing={1.25} alignItems="flex-start">
      <Box
        sx={{ color: toneStyles[tone].foreground, display: "flex", mt: "2px" }}
      >
        {annunciatorIcon(tone)}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="caption"
          sx={{
            color: toneStyles[tone].foreground,
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "0.08em",
          }}
        >
          {tone}
        </Typography>
        <Typography
          variant="subtitle2"
          sx={{ color: "var(--text)", fontWeight: 700 }}
        >
          {title}
        </Typography>
        <Typography
          component="div"
          variant="body2"
          sx={{ color: "var(--muted)" }}
        >
          {message}
        </Typography>
      </Box>
      {action}
    </Stack>
  </Paper>
);
